import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Loader2, Mail, MapPin, Phone, Ship } from "lucide-react";
import { Avatar, Card, Logo } from "@/components/common";
import { daysUntil, fmtDate, fmtShort, itinerary, returnDate } from "@/lib/trip";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvite() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/invite/${token}`)
      .then(({ data }) => { setData(data); setState("ready"); })
      .catch(() => setState("error"));
  }, [token]);

  if (state === "loading") return <div className="grid min-h-screen place-items-center bg-[#F5F7FA]"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div>;
  if (state === "error") return (
    <div className="grid min-h-screen place-items-center bg-[#F5F7FA] px-6 text-center">
      <div>
        <p className="font-display text-xl font-bold text-navy-900">Trip not found</p>
        <p className="mt-2 text-sm text-slate-500">This link may have expired. Please ask your friend for an updated link.</p>
      </div>
    </div>
  );

  const t = data.trip;
  const advisor = data.advisor || {};
  const advisorAvatar = advisor.avatar && advisor.id ? `${BACKEND}/api/public/agents/${advisor.id}/avatar` : null;
  const d = daysUntil(t.departure);
  const days = itinerary(t);

  return (
    <div className="min-h-screen bg-[#F5F7FA] py-6 sm:py-10" data-testid="public-invite">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-4 flex justify-center"><Logo brand="mytrip" sub="Powered by Topsail Travel" center /></div>

        <Card className="overflow-hidden">
          <div className="relative h-52 sm:h-72">
            <img src={t.image || "/images/sunset.jpg"} alt={t.destination} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/30 to-transparent" />
            <span className="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-navy-900 backdrop-blur">You're invited</span>
            <div className="absolute bottom-5 left-5 right-5 text-white">
              {data.inviterName && <p className="text-xs text-white/75" data-testid="public-invite-inviter">{data.inviterName.split(" ")[0]} is heading to</p>}
              <h1 className="font-display text-2xl font-bold sm:text-3xl" data-testid="public-invite-title">{t.title || t.destination}</h1>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-white/85">
                {t.vessel && <><Ship className="h-3.5 w-3.5" />{t.vessel} · </>}{fmtShort(t.departure)} – {fmtDate(returnDate(t))}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-slate-100 py-5" data-testid="public-invite-countdown">
            {[[d < 0 ? 0 : d, d === 1 ? "Day to go" : "Days to go"], [fmtShort(t.departure), "Departure"], [`${t.nights}`, "Nights"]].map(([v, l]) => (
              <div key={l} className="min-w-0 px-2 text-center"><p className="truncate font-display text-2xl font-bold text-navy-900 sm:text-3xl">{v}</p><p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{l}</p></div>
            ))}
          </div>
        </Card>

        {days.length > 0 && (
          <Card className="mt-5">
            <div className="flex items-center gap-2 border-b border-slate-100 p-5"><CalendarDays className="h-4 w-4 text-navy-700" /><h2 className="text-sm font-semibold text-navy-900">Itinerary overview</h2></div>
            <ol className="space-y-2 p-5" data-testid="public-invite-itinerary">
              {days.map((day) => (
                <li key={day.day} className="flex gap-3 text-sm">
                  <span className="w-12 shrink-0 text-xs font-semibold leading-5 text-slate-400">Day {day.day}</span>
                  <span className="min-w-0 text-navy-900"><MapPin className="mr-1 inline h-3 w-3 text-slate-300" />{day.title}{day.place && day.place !== day.title && <span className="text-slate-400"> · {day.place}</span>}</span>
                </li>
              ))}
            </ol>
          </Card>
        )}

        <Card className="mt-5 p-5" data-testid="public-invite-advisor">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Planned by</p>
          <div className="mt-3 flex items-center gap-3">
            <Avatar name={advisor.name} src={advisorAvatar} size="lg" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-navy-900">{advisor.name}</p>
              <p className="truncate text-xs text-slate-500">{[advisor.title, data.agencyName].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            {advisor.email && <a href={`mailto:${advisor.email}`} data-testid="public-invite-email" className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-navy-900 text-sm font-semibold text-white transition-colors hover:bg-navy-800"><Mail className="h-4 w-4" />Email {advisor.name.split(" ")[0]}</a>}
            {advisor.phone && <a href={`tel:${advisor.phone}`} data-testid="public-invite-phone" className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-semibold text-navy-900 transition-colors hover:bg-slate-50"><Phone className="h-4 w-4" />Call</a>}
          </div>
          <p className="mt-4 text-center text-xs text-slate-400">Want to join or plan your own trip? Reach out to {advisor.name.split(" ")[0]} — they'll take care of the rest.</p>
        </Card>
      </div>
    </div>
  );
}
